/* Hallmark · component: security-action-dialog · genre: editorial · theme: Operational Architect
 * states: default · hover · focus · active · disabled · loading · error · success
 * contrast: pass via existing semantic surface and text tokens
 */
import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, KeyRound, Loader2, Mail, MessageCircle, X } from 'lucide-react';
import { Button } from '@lms/ui';
import { Bilingual } from './Bilingual';
import { copy } from '../copy/copy';
import { useLanguage } from '../lib/LanguageContext';

type RequestState = 'idle' | 'loading' | 'error' | 'success';

interface ParticipantPasswordResetDialogProps {
  participantName: string;
  participantEmail: string;
  onClose: () => void;
  onSend: () => Promise<void>;
  onOpenCommunication: (channel: 'whatsapp' | 'email') => void;
}

export function ParticipantPasswordResetDialog({
  participantName,
  participantEmail,
  onClose,
  onSend,
  onOpenCommunication,
}: ParticipantPasswordResetDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [state, setState] = useState<RequestState>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const { t } = useLanguage();

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (!dialog.open) dialog.showModal();

    return () => {
      if (dialog.open) dialog.close();
    };
  }, []);

  const handleClose = () => {
    if (state !== 'loading') onClose();
  };

  const handleSubmit = async () => {
    setState('loading');
    setErrorMessage('');
    try {
      await onSend();
      setState('success');
    } catch (error) {
      setState('error');
      setErrorMessage(
        error instanceof Error ? error.message : t(copy.users.passwordReset.errorMessage)
      );
    }
  };

  return (
    <dialog
      ref={dialogRef}
      className="m-auto w-[calc(100%-2rem)] max-w-lg rounded-[1.75rem] bg-surface-container-lowest p-0 text-on-surface shadow-level5 backdrop:bg-on-surface/45 backdrop:backdrop-blur-sm"
      aria-labelledby="participant-password-reset-title"
      onCancel={event => {
        event.preventDefault();
        handleClose();
      }}
      onClick={event => {
        if (event.target === event.currentTarget) handleClose();
      }}
    >
      <div className="space-y-6 p-6 sm:p-8">
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 items-start gap-3">
            <div className="grid size-11 shrink-0 place-items-center rounded-2xl bg-primary/10 text-primary">
              <KeyRound size={21} aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.16em] text-primary">
                <Bilingual {...copy.users.passwordReset.eyebrow} />
              </p>
              <h2
                id="participant-password-reset-title"
                className="mt-1 font-headline text-xl font-bold tracking-tight text-on-surface"
              >
                <Bilingual {...copy.users.passwordReset.title} />
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={state === 'loading'}
            aria-label={t(copy.users.passwordReset.closeLabel)}
            className="grid size-11 shrink-0 place-items-center rounded-xl text-on-surface-variant transition-colors duration-150 hover:bg-surface-container-high hover:text-on-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-50 active:scale-95"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        {state === 'success' ? (
          <div
            role="status"
            className="rounded-2xl bg-success-container p-5 text-on-success-container"
          >
            <CheckCircle2 size={22} aria-hidden="true" />
            <p className="mt-3 text-sm font-label font-bold">
              <Bilingual {...copy.users.passwordReset.successTitle} />
            </p>
            <p className="mt-1 text-xs leading-relaxed">
              {t(copy.users.passwordReset.successMessage).replace('{email}', participantEmail)}
            </p>
            <div className="mt-5 grid gap-2 sm:grid-cols-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenCommunication('whatsapp')}
                className="h-10 rounded-xl border-on-success-container/20 bg-on-success-container/10 px-3 text-xs font-label font-bold text-on-success-container hover:bg-on-success-container/20"
              >
                <MessageCircle size={15} />
                <Bilingual {...copy.users.passwordReset.shareViaWhatsApp} />
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenCommunication('email')}
                className="h-10 rounded-xl border-on-success-container/20 bg-on-success-container/10 px-3 text-xs font-label font-bold text-on-success-container hover:bg-on-success-container/20"
              >
                <Mail size={15} />
                <Bilingual {...copy.users.passwordReset.shareViaEmail} />
              </Button>
            </div>
          </div>
        ) : (
          <>
            <p className="m-0 text-sm leading-relaxed text-on-surface-variant">
              <Bilingual {...copy.users.passwordReset.description} />
            </p>
            <div className="rounded-2xl bg-surface-container-low p-4">
              <p className="text-[10px] font-label font-bold uppercase tracking-[0.14em] text-on-surface-variant">
                <Bilingual {...copy.users.passwordReset.recipientLabel} />
              </p>
              <div className="mt-2 flex min-w-0 items-center gap-2 text-sm font-semibold text-on-surface">
                <Mail size={16} className="shrink-0 text-primary" aria-hidden="true" />
                <span className="min-w-0 truncate">
                  {participantName} · {participantEmail}
                </span>
              </div>
            </div>
            <p className="m-0 rounded-xl bg-primary/5 px-4 py-3 text-xs leading-relaxed text-on-surface-variant">
              <Bilingual {...copy.users.passwordReset.securityNotice} />
            </p>
            {state === 'error' && (
              <p
                role="alert"
                className="m-0 rounded-xl bg-error/10 px-4 py-3 text-xs font-medium leading-relaxed text-error"
              >
                {errorMessage}
              </p>
            )}
          </>
        )}

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="ghost"
            onClick={handleClose}
            disabled={state === 'loading'}
            className="h-11 rounded-xl px-4 text-xs font-label font-bold active:scale-95"
          >
            <Bilingual {...(state === 'success' ? copy.common.close : copy.common.cancel)} />
          </Button>
          {state !== 'success' && (
            <Button
              type="button"
              variant="primary"
              onClick={() => void handleSubmit()}
              disabled={state === 'loading'}
              className="h-11 rounded-xl px-5 text-xs font-label font-bold active:scale-95"
            >
              {state === 'loading' ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Mail size={16} />
              )}
              <Bilingual
                {...(state === 'loading'
                  ? copy.users.passwordReset.sendingAction
                  : copy.users.passwordReset.sendAction)}
              />
            </Button>
          )}
        </div>
      </div>
    </dialog>
  );
}
